import React from 'react';
import Icon from '../../../components/AppIcon';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from 'recharts';

const KPIDashboard = ({ project }) => {
  const kpiData = [
    {
      label: 'Emisiones Reducidas',
      value: project?.totalEmissionsReduced || 0,
      unit: 'tCO₂e/año',
      icon: 'TrendingDown',
      color: 'var(--color-success)',
      change: '+12.5%',
      changeType: 'positive'
    },
    {
      label: 'Eficiencia por Hectárea',
      value: project?.emissionsPerHectare || 0,
      unit: 'tCO₂e/ha',
      icon: 'BarChart3',
      color: 'var(--color-accent)',
      change: '+8.3%',
      changeType: 'positive'
    },
    {
      label: 'Progreso del Proyecto',
      value: project?.progress || 0,
      unit: '%',
      icon: 'Target',
      color: 'var(--color-primary)',
      change: '+5.2%',
      changeType: 'positive'
    },
    {
      label: 'Créditos Generados',
      value: project?.creditsGenerated || 0,
      unit: 'créditos',
      icon: 'Award',
      color: 'var(--color-warning)',
      change: '+15.7%',
      changeType: 'positive'
    }
  ];

  const monthlyData = [
    { month: 'Ene', emissions: 2400, target: 2200 },
    { month: 'Feb', emissions: 2600, target: 2400 },
    { month: 'Mar', emissions: 2800, target: 2600 },
    { month: 'Abr', emissions: 3100, target: 2800 },
    { month: 'May', emissions: 3300, target: 3000 },
    { month: 'Jun', emissions: 3500, target: 3200 }
  ];

  const yearlyComparison = [
    { year: '2022', value: 28000 },
    { year: '2023', value: 32000 },
    { year: '2024', value: 36500 },
    { year: '2025', value: 41200 },
    { year: '2026', value: 45800 }
  ];

  return (
    <div className="card">
      <div className="flex items-center gap-3 mb-6">
        <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-lg">
          <Icon name="BarChart3" size={20} color="var(--color-accent)" />
        </div>
        <h2 className="text-xl font-semibold font-mono">Indicadores Clave de Desempeño</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {kpiData?.map((kpi, index) => (
          <div
            key={index}
            className="p-4 bg-background rounded-lg border border-border hover:shadow-md transition-all duration-250"
          >
            <div className="flex items-start justify-between mb-3">
              <div
                className="flex items-center justify-center w-10 h-10 rounded-lg"
                style={{ backgroundColor: `${kpi?.color}15` }}
              >
                <Icon name={kpi?.icon} size={20} color={kpi?.color} />
              </div>
              <div
                className={`flex items-center gap-1 px-2 py-1 rounded caption text-xs ${
                  kpi?.changeType === 'positive' ?'bg-success/10 text-success' :'bg-error/10 text-error'
                }`}
              >
                <Icon
                  name={kpi?.changeType === 'positive' ? 'TrendingUp' : 'TrendingDown'}
                  size={12}
                />
                <span>{kpi?.change}</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground caption mb-2">{kpi?.label}</p>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-semibold text-foreground data-text">
                {kpi?.value?.toLocaleString('es-ES')}
              </span>
              <span className="text-xs text-muted-foreground caption">{kpi?.unit}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="p-4 bg-background rounded-lg border border-border">
          <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
            <Icon name="TrendingUp" size={16} color="var(--color-primary)" />
            Emisiones Mensuales vs Objetivo
          </h3>
          <div className="w-full h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis
                  dataKey="month"
                  stroke="var(--color-muted-foreground)"
                  style={{ fontSize: '12px' }}
                />
                <YAxis
                  stroke="var(--color-muted-foreground)"
                  style={{ fontSize: '12px' }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--color-card)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '6px'
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="emissions"
                  stroke="var(--color-accent)"
                  strokeWidth={2}
                  name="Emisiones Reducidas"
                />
                <Line
                  type="monotone"
                  dataKey="target"
                  stroke="var(--color-primary)"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  name="Objetivo"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="p-4 bg-background rounded-lg border border-border">
          <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
            <Icon name="BarChart2" size={16} color="var(--color-success)" />
            Comparación Anual de Reducciones
          </h3>
          <div className="w-full h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={yearlyComparison}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis
                  dataKey="year"
                  stroke="var(--color-muted-foreground)"
                  style={{ fontSize: '12px' }}
                />
                <YAxis
                  stroke="var(--color-muted-foreground)"
                  style={{ fontSize: '12px' }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--color-card)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '6px'
                  }}
                />
                <Bar dataKey="value" fill="var(--color-success)" name="tCO₂e/año" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KPIDashboard;